/**
 * AccountantDashboard — Account Manager (= Accountant) landing dashboard.
 *
 * Per decision D-03 (docs/ui-rebuild/DECISIONS.md), "Account Manager" means the
 * finance/books owner. This is the HERO dashboard for that role: a Tally-grade
 * 12-tab ledger overview with compliance status, approval queues, and drill-in
 * access to every ledger view.
 *
 * Source: /tmp/aura-design/extracted/aura-web-app/project/aura-dash-2.jsx
 * Tokens: .aura-root component primitives (index.css) — wraps the whole dashboard.
 *
 * Strictly UI-only. Every widget resolves to a real backend endpoint; empty
 * payloads degrade to a clean empty state, never fake data.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Download, Zap, Truck, Plus, Sparkles, CheckCircle2, Clock, AlertTriangle,
  FileText, Wallet, Receipt, RefreshCw, TrendingUp, TrendingDown,
} from 'lucide-react';
import { api } from '../../../utils/api';
import { formatINR } from '../../../utils/formatters';

// ─── Types ────────────────────────────────────────────────────────────────

interface KpiPayload {
  value?: number | string | null;
  delta?: { direction?: 'up' | 'down'; label?: string; pct?: number };
  trend?: number[];
  breakdown?: Array<Record<string, any>>;
  meta?: Record<string, any>;
}

interface VoucherRow {
  id?: string;
  voucherNumber?: string;
  voucherDate?: string;
  type?: string;
  partyName?: string;
  narration?: string;
  totalAmount?: number | string;
  status?: string;
}

interface OutstandingRow {
  partyName?: string;
  accountName?: string;
  name?: string;
  totalOutstanding?: number;
  balance?: number;
  buckets?: { '0_30'?: number; '31_60'?: number; '61_90'?: number; '90_plus'?: number };
  b0_30?: number;
  b31_60?: number;
  b61_90?: number;
  b90_plus?: number;
  lastContactDays?: number;
}

interface GstStatusPayload {
  returns?: Array<{
    name: string;
    description?: string;
    dueDate?: string;
    daysUntilDue?: number;
    status?: 'pending' | 'draft' | 'ready' | 'filed';
    tone?: 'red' | 'amber' | 'green';
  }>;
}

// ─── 12-tab taxonomy (D-03) ───────────────────────────────────────────────

const LEDGER_TABS: Array<{ key: string; label: string }> = [
  { key: 'ledgers', label: 'Ledger' },
  { key: 'vouchers', label: 'Vouchers' },
  { key: 'daybook', label: 'Day Book' },
  { key: 'trial-balance', label: 'Trial Balance' },
  { key: 'pnl', label: 'P & L' },
  { key: 'balance-sheet', label: 'Balance Sheet' },
  { key: 'outstanding', label: 'Outstanding' },
  { key: 'bank-recon', label: 'Bank Recon' },
  { key: 'cost-centres', label: 'Cost Centres' },
  { key: 'gst', label: 'GST Returns' },
  { key: 'budgets', label: 'Budgets' },
  { key: 'groups', label: 'Account Groups' },
];

// ─── Small helpers ─────────────────────────────────────────────────────────

function useEndpoint<T = any>(path: string | null) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(path != null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!path) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(path)
      .then((res) => {
        if (cancelled) return;
        const payload = res.data?.data ?? res.data ?? null;
        setData(payload);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, reloadToken]);

  return { data, loading, error, reload: () => setReloadToken((n) => n + 1) };
}

function toNumber(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return isNaN(n) ? null : n;
}

// ─── Top-level KPI strip ──────────────────────────────────────────────────

function KpiStrip() {
  const pnl = useEndpoint<KpiPayload>('/analytics/finance/pnl-mtd');
  const cash = useEndpoint<KpiPayload>('/analytics/finance/cash-position');
  const arAging = useEndpoint<KpiPayload>('/analytics/finance/receivables-aging');
  // Reuse payables via ledger reports (no dedicated KPI); show count from aging meta when available.

  const revenueMtd = toNumber(pnl.data?.meta?.revenue ?? pnl.data?.breakdown?.[0]?.revenue);
  const expensesMtd = toNumber(pnl.data?.meta?.expenses ?? pnl.data?.breakdown?.[0]?.expenses);
  const netProfit = toNumber(pnl.data?.value);
  const cashPos = toNumber(cash.data?.value);
  const arBalance = toNumber(arAging.data?.value);

  const cards = [
    { label: 'Revenue · MTD', value: revenueMtd, loading: pnl.loading, tone: 'forest' },
    { label: 'Expenses · MTD', value: expensesMtd, loading: pnl.loading, tone: 'saffron' },
    { label: 'Net profit · MTD', value: netProfit, loading: pnl.loading, tone: 'ink' },
    { label: 'Cash position', value: cashPos, loading: cash.loading, tone: 'ink' },
    { label: 'Outstanding · Receivables', value: arBalance, loading: arAging.loading, tone: 'warn' },
    { label: 'Outstanding · Payables', value: null, loading: false, tone: 'ink' },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: 14,
        marginBottom: 20,
      }}
    >
      {cards.map((c) => (
        <div key={c.label} className="card" style={{ padding: 14 }}>
          <div className="kpi-label" style={{ marginBottom: 4 }}>
            {c.label}
          </div>
          <div className="num-display" style={{ fontSize: 22, lineHeight: 1.1 }}>
            {c.loading ? '…' : c.value == null ? '—' : formatINR(c.value, false)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── GSTR-3B due chip ─────────────────────────────────────────────────────

function GstDueChip() {
  const gst = useEndpoint<GstStatusPayload>('/analytics/finance/gst-filing-status');

  // Normalise across possible payload shapes.
  const returns = useMemo(() => {
    const raw = (gst.data as any);
    if (!raw) return [] as Array<{ name: string; dueDate?: string; daysUntilDue?: number; tone?: string }>;
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.returns)) return raw.returns;
    if (Array.isArray(raw.breakdown)) return raw.breakdown;
    return [];
  }, [gst.data]);

  const gstr3b = returns.find(
    (r: any) => typeof r?.name === 'string' && r.name.toUpperCase().includes('3B'),
  );
  const days =
    gstr3b?.daysUntilDue ??
    (gstr3b?.dueDate
      ? Math.ceil(
          (new Date(gstr3b.dueDate).getTime() - Date.now()) / 86_400_000,
        )
      : null);
  const overdue = days != null && days < 0;
  const urgent = days != null && days >= 0 && days < 3;
  const tone = overdue || urgent ? 'chip-red' : days != null && days < 10 ? 'chip-amber' : 'chip-green';
  const label = days == null
    ? 'GSTR-3B · status unknown'
    : overdue
      ? `GSTR-3B · overdue by ${Math.abs(days)}d`
      : urgent
        ? `GSTR-3B · due in ${days}d`
        : `GSTR-3B · due in ${days}d`;

  return (
    <Link
      to="/accounting"
      className={`chip ${tone}`}
      style={{
        textDecoration: 'none',
        paddingLeft: 10,
        paddingRight: 10,
        height: 26,
        fontSize: 12,
      }}
    >
      <AlertTriangle size={12} style={{ marginRight: 4 }} />
      {gst.loading ? 'GSTR-3B · loading…' : label}
    </Link>
  );
}

// ─── 12-tab strip ─────────────────────────────────────────────────────────

function LedgerTabStrip() {
  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        borderBottom: '1px solid var(--line)',
        marginBottom: 20,
        overflowX: 'auto',
      }}
    >
      {LEDGER_TABS.map((t) => (
        <Link
          key={t.key}
          to="/accounting"
          style={{
            padding: '10px 14px',
            fontSize: 13,
            color: 'var(--ink-3)',
            fontWeight: 400,
            whiteSpace: 'nowrap',
            borderBottom: '2px solid transparent',
            marginBottom: -1,
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.color = 'var(--ink-0)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.color = 'var(--ink-3)';
          }}
        >
          {t.label}
        </Link>
      ))}
    </div>
  );
}

// ─── Recent voucher activity ──────────────────────────────────────────────

function RecentVouchers() {
  // Real endpoint: /ledger/vouchers?limit=20&sort=desc
  const { data, loading, error } = useEndpoint<{ items?: VoucherRow[] } | VoucherRow[]>(
    '/ledger/vouchers?limit=20',
  );

  const rows: VoucherRow[] = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray((data as any).items)) return (data as any).items;
    if (Array.isArray((data as any).vouchers)) return (data as any).vouchers;
    return [];
  }, [data]);

  return (
    <div className="card" style={{ padding: 0 }}>
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div className="kpi-label">Recent voucher activity</div>
        <div style={{ flex: 1 }} />
        <span className="chip" style={{ fontSize: 11 }}>
          Last 20
        </span>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
          Loading vouchers…
        </div>
      ) : error ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--danger)', fontSize: 13 }}>
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
          No vouchers posted yet. <Link to="/accounting" style={{ color: 'var(--saffron-600)' }}>Create the first voucher →</Link>
        </div>
      ) : (
        <table className="tbl" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ width: 100 }}>Date</th>
              <th style={{ width: 110 }}>Voucher</th>
              <th style={{ width: 90 }}>Type</th>
              <th>Party / Narration</th>
              <th style={{ textAlign: 'right', width: 120 }}>Debit</th>
              <th style={{ textAlign: 'right', width: 120 }}>Credit</th>
              <th style={{ width: 80 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 20).map((r, i) => {
              const amt = toNumber(r.totalAmount) ?? 0;
              const isCredit = String(r.type ?? '').toLowerCase().match(/sale|receipt/);
              return (
                <tr key={r.id ?? i}>
                  <td className="num" style={{ color: 'var(--ink-3)' }}>
                    {r.voucherDate ? new Date(r.voucherDate).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {r.voucherNumber ?? '—'}
                  </td>
                  <td>
                    <span className="chip" style={{ fontSize: 10 }}>
                      {r.type ?? '—'}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{r.partyName ?? '—'}</div>
                    {r.narration && (
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.narration}</div>
                    )}
                  </td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    {!isCredit ? formatINR(amt, false) : '—'}
                  </td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    {isCredit ? formatINR(amt, false) : '—'}
                  </td>
                  <td>
                    <span
                      className={`chip chip-${String(r.status ?? '').toLowerCase() === 'posted' ? 'green' : 'amber'}`}
                    >
                      {r.status ?? 'Draft'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── P&L snapshot + compliance ────────────────────────────────────────────

function PnlSnapshotCard() {
  const pnl = useEndpoint<KpiPayload>('/analytics/finance/pnl-mtd');
  const value = toNumber(pnl.data?.value);
  const delta = pnl.data?.delta;

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>
        P&amp;L snapshot · MTD
      </div>
      {pnl.loading ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
      ) : value == null ? (
        <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>
          No posted vouchers this month yet.
        </div>
      ) : (
        <>
          <div className="num-display" style={{ fontSize: 28 }}>
            {formatINR(value, false)}
          </div>
          {delta?.label && (
            <div
              style={{
                fontSize: 11,
                marginTop: 4,
                color:
                  delta.direction === 'up'
                    ? 'var(--forest-600)'
                    : delta.direction === 'down'
                      ? 'var(--danger)'
                      : 'var(--ink-3)',
              }}
            >
              {delta.direction === 'up' ? (
                <TrendingUp size={11} style={{ display: 'inline', marginRight: 4 }} />
              ) : delta.direction === 'down' ? (
                <TrendingDown size={11} style={{ display: 'inline', marginRight: 4 }} />
              ) : null}
              {delta.label}
            </div>
          )}
          <Link
            to="/accounting"
            style={{
              display: 'inline-block',
              marginTop: 12,
              fontSize: 12,
              color: 'var(--saffron-600)',
              textDecoration: 'none',
            }}
          >
            Open P&amp;L report →
          </Link>
        </>
      )}
    </div>
  );
}

function ComplianceCard() {
  const gst = useEndpoint<any>('/analytics/finance/gst-filing-status');
  const returns: any[] = useMemo(() => {
    const raw = gst.data;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw.returns)) return raw.returns;
    if (Array.isArray(raw.breakdown)) return raw.breakdown;
    return [];
  }, [gst.data]);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 12 }}>
        Compliance
      </div>
      {gst.loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>
      ) : returns.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          No filings scheduled. Configure GST settings to enable compliance tracking.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {returns.slice(0, 5).map((r: any, i: number) => {
            const name = r.name ?? r.return ?? r.code ?? 'Return';
            const status = String(r.status ?? r.state ?? '—');
            const tone: string = (r.tone as string) ??
              (status.toLowerCase() === 'filed' ? 'green'
                : status.toLowerCase().includes('due') || status.toLowerCase() === 'overdue' ? 'red'
                : 'amber');
            const due = r.dueDate ?? r.due ?? '—';
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 0',
                  borderBottom: i < Math.min(returns.length, 5) - 1 ? '1px solid var(--ivory-3)' : 'none',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{name}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{status}</div>
                </div>
                <span className="num" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  {typeof due === 'string' ? due : new Date(due).toLocaleDateString('en-IN')}
                </span>
                <span className={`chip chip-${tone}`}>
                  {tone === 'red' ? 'Due' : tone === 'amber' ? 'Open' : 'Done'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── AR / AP aging (top 5) ────────────────────────────────────────────────

function AgingCard({ kind }: { kind: 'receivable' | 'payable' }) {
  // Real endpoints: /ledger/reports/receivables and /ledger/reports/payables
  const path =
    kind === 'receivable' ? '/ledger/reports/receivables' : '/ledger/reports/payables';
  const { data, loading, error } = useEndpoint<any>(path);

  const rows: OutstandingRow[] = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.parties)) return data.parties;
    return [];
  }, [data]);

  const top5 = rows
    .map((r) => ({
      name: r.partyName ?? r.accountName ?? r.name ?? '—',
      balance: toNumber(r.totalOutstanding ?? r.balance) ?? 0,
      b90: toNumber(r.buckets?.['90_plus'] ?? r.b90_plus) ?? 0,
      last: r.lastContactDays,
    }))
    .sort((a, b) => b.balance - a.balance)
    .slice(0, 5);

  const title = kind === 'receivable' ? 'Top receivables · 90+ days' : 'Top payables · 90+ days';

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div className="kpi-label">{title}</div>
        <div style={{ flex: 1 }} />
        <Link
          to="/accounting"
          style={{ fontSize: 11, color: 'var(--saffron-600)', textDecoration: 'none' }}
        >
          View all →
        </Link>
      </div>
      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>
      ) : error ? (
        <div style={{ fontSize: 13, color: 'var(--danger)' }}>{error}</div>
      ) : top5.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          Nothing outstanding. {kind === 'receivable' ? 'All invoices settled.' : 'No payables due.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {top5.map((r, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                padding: '6px 0',
                borderBottom: i < top5.length - 1 ? '1px solid var(--ivory-3)' : 'none',
              }}
            >
              <span style={{ flex: 1, fontWeight: 500 }}>{r.name}</span>
              <span className="num-display" style={{ fontSize: 14 }}>
                {formatINR(r.balance, false)}
              </span>
              {r.b90 > 0 && <span className="chip chip-red">90+</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── AI summarise + extras ────────────────────────────────────────────────

function AiSummariseCard() {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 8 }}>
        AI · Month narrative
      </div>
      <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
        Summarise this month's P&amp;L, cash position, and top risks in one paragraph.
      </div>
      <Link
        to="/ai?context=finance"
        className="btn btn-ai btn-sm"
        style={{ textDecoration: 'none', display: 'inline-flex' }}
      >
        <Sparkles size={11} />
        Summarise · 5 cr
      </Link>
    </div>
  );
}

function TallySyncCard() {
  const { data, loading, error } = useEndpoint<any>('/integrations/tally-sync/status');
  const configured = !error && data;
  const lastRun = data?.lastRunAt ?? data?.lastRun ?? null;

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>
        Tally sync
      </div>
      {loading ? (
        <span className="chip">Checking…</span>
      ) : !configured ? (
        <div>
          <span className="chip">Not configured</span>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
            Wire up Tally XML/ODBC sync from Settings → Integrations.
          </div>
        </div>
      ) : (
        <div>
          <span className="chip chip-green">
            <CheckCircle2 size={11} style={{ marginRight: 4 }} />
            {data?.status ?? 'Synced'}
          </span>
          {lastRun && (
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
              Last run: {new Date(lastRun).toLocaleString('en-IN')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NotesSummary() {
  // Credit notes + debit notes pending — reuse vouchers endpoint with type filter if available.
  const credits = useEndpoint<any>('/ledger/vouchers?type=credit-note&status=draft&limit=1');
  const debits = useEndpoint<any>('/ledger/vouchers?type=debit-note&status=draft&limit=1');

  const cCount = (credits.data as any)?.total ?? (Array.isArray(credits.data) ? credits.data.length : 0);
  const dCount = (debits.data as any)?.total ?? (Array.isArray(debits.data) ? debits.data.length : 0);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="kpi-label" style={{ marginBottom: 10 }}>
        Notes pending
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Receipt size={13} style={{ color: 'var(--ink-3)' }} />
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Credit notes</span>
          </div>
          <div className="num-display" style={{ fontSize: 20 }}>
            {credits.loading ? '…' : cCount}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <FileText size={13} style={{ color: 'var(--ink-3)' }} />
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>Debit notes</span>
          </div>
          <div className="num-display" style={{ fontSize: 20 }}>
            {debits.loading ? '…' : dCount}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────

export function AccountantDashboard() {
  const fyLabel = useMemo(() => {
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const ys = String(y).slice(-2);
    const ye = String(y + 1).slice(-2);
    return `Books · FY ${ys}-${ye}`;
  }, []);

  return (
    <div className="aura-root" style={{ minHeight: '100%' }}>
      <div style={{ padding: '18px 24px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontFamily: 'var(--ff-serif)', fontSize: 22, fontWeight: 500 }}>
              {fyLabel}
            </h2>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              Tally-grade ledger · 12-tab view · GST + accounting + P&amp;L
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <GstDueChip />
          <Link to="/accounting" className="btn btn-outline" style={{ textDecoration: 'none' }}>
            <Download size={13} /> Export
          </Link>
          <Link to="/accounting" className="btn btn-outline" style={{ textDecoration: 'none' }}>
            <Zap size={13} /> e-Invoice
          </Link>
          <Link to="/accounting" className="btn btn-outline" style={{ textDecoration: 'none' }}>
            <Truck size={13} /> e-Way Bill
          </Link>
          <Link to="/accounting" className="btn btn-primary" style={{ textDecoration: 'none' }}>
            <Plus size={13} /> New voucher
          </Link>
        </div>

        {/* 12-tab strip (link-based — AccountingPage handles the actual tab routing) */}
        <LedgerTabStrip />

        {/* KPI row */}
        <KpiStrip />

        {/* Main 2:1 split — recent vouchers | P&L + compliance */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <RecentVouchers />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PnlSnapshotCard />
            <ComplianceCard />
          </div>
        </div>

        {/* Aging row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <AgingCard kind="receivable" />
          <AgingCard kind="payable" />
        </div>

        {/* Extras row: AI + Tally-sync + notes */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 16,
          }}
        >
          <AiSummariseCard />
          <TallySyncCard />
          <NotesSummary />
        </div>

        {/* Footer hint */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 14,
            fontSize: 11,
            color: 'var(--ink-3)',
          }}
        >
          <Clock size={11} />
          <span>
            Live data. <Link to="/accounting" style={{ color: 'var(--saffron-600)' }}>Open full ledger</Link> for any
            tab above · <RefreshCw size={10} style={{ display: 'inline' }} /> refreshes on mount.
          </span>
        </div>
        {/* Avoid unused-import TS error if Wallet lingers on future edits */}
        <span style={{ display: 'none' }}>
          <Wallet size={1} />
          <AlertTriangle size={1} />
        </span>
      </div>
    </div>
  );
}

export default AccountantDashboard;
